// ProfileHub — U4 (2026-07-09) rebuild: hub-and-spoke, no tab bar.
//
// The old 4-tab strip (वॉलेट / जमा 🫙 / Rates / खाता) is gone. /profile now
// lands on a plain card MENU (ProfileLandingPanel); each card opens its own
// full page and shows a "← back" bar instead of tabs:
//
//   /profile          → ProfileLandingPanel (card menu + danger banner)
//   /profile/wallet   → MyWalletPanel
//   /profile/deposit  → MyDepositPanel   (single home of the deposit)
//   /profile/rates    → MyRatesPanel
//   /profile/rewards  → ProfileRewardsPanel (folded from the old /rewards tab)
//   /profile/account  → AccountPanel
//
// Why hub-and-spoke: one decision per screen. The landing answers "where
// is the thing I need?" with five labelled cards; each spoke answers its
// one question with full depth. No nested tabs, no duplicate surfaces.
//
// Wallet sync: refreshAgentWallet() still fires on every pathname change
// (matches PlayersHub / AgentsHub) so the chrome chip and the landing
// card are always fresh.

import { useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import Icon from '@baaligames/shared-ui/components/Icon';
import HubHeader from '@baaligames/shared-ui/admin/components/HubHeader';
import { T } from '@baaligames/shared-ui/admin/lib/textStyles';
import { refreshAgentWallet } from '@baaligames/shared-ui/admin/lib/agentWallet';
import { useT } from '@baaligames/shared-ui/lib/i18n';

// Spoke titles — keyed by the path segment after /profile/.
const SPOKE_TITLE_KEYS: Record<string, string> = {
  wallet:  'agent.profile.landing.wallet.title',
  deposit: 'agent.profile.landing.deposit.title',
  rates:   'agent.profile.landing.rates.title',
  rewards: 'agent.profile.landing.rewards.title',
  account: 'agent.profile.landing.account.title',
};

export default function ProfileHub() {
  const t = useT();
  const loc = useLocation();

  useEffect(() => {
    refreshAgentWallet();
  }, [loc.pathname]);

  const spoke = loc.pathname.replace(/^\/profile\/?/, '').split('/')[0];
  const onLanding = !spoke;

  return (
    <div className="max-w-xl lg:max-w-3xl mx-auto px-2 sm:px-3 lg:px-6 py-3 lg:py-4 space-y-4">
      {onLanding ? (
        <HubHeader
          icon="account_balance_wallet" tone="brand"
          title={t('agent.profile.hub.title')}
          subtitle={t('agent.profile.hub.subtitle')}
        />
      ) : (
        /* Spoke pages: slim back bar instead of the hub header — one tap
           back to the card menu, current page named right next to it. */
        <div className="px-2 lg:px-4 flex items-center gap-2">
          <Link
            to="/profile"
            className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5
                        text-[13px] font-bold ${T.secondary}
                        bg-arena-100/70 dark:bg-white/[0.07]
                        ring-1 ring-inset ring-arena-200/60 dark:ring-white/10
                        hover:bg-arena-100 dark:hover:bg-white/10 transition`}
          >
            <Icon name="arrow_back" size={16} />
            {t('agent.profile.hub.title')}
          </Link>
          <span className={`text-[15px] font-display font-semibold ${T.primary}`}>
            {SPOKE_TITLE_KEYS[spoke] ? t(SPOKE_TITLE_KEYS[spoke]) : ''}
          </span>
        </div>
      )}

      {/* Active panel */}
      <div className="px-2 lg:px-4">
        <Outlet />
      </div>
    </div>
  );
}
